import * as React from 'react'
import { AppState } from '@halo/stores/app.state'
import { AppService, DomainService } from '@halo/services/index'
import { Container } from 'typescript-ioc'
import { Drawer, executeCleanDrawerRender } from '@pushly/aqe/lib/components'
import { classnames as getClassNames } from '../../../_utils/classnames'
import {
    DomainSendIntegrationModel,
    IDomainSendIntegrationModel,
} from '@halo/models/send-integration-configurations/domain-send-integration.model'
import { DomainDto } from '@halo/dtos/domain.dto'
import { WebApnsConfigurationModel } from '@halo/models/send-integration-configurations/web-apns-configuration.model'
import { Form } from 'antd'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '@halo/constants'
import { simpleNotification } from '@halo/_utils/notifications'
import { FcmSendIntegrationApiVersion, SendIntegrationPrivateKeyType } from '@halo/interfaces/send-integrations'
import {
    NativeP12ApnsConfiguration,
    NativeP8ApnsConfiguration,
} from '@halo/models/send-integration-configurations/native-apns-configurations.model'
import {
    NativeLegacyFcmConfiguration,
    NativeV1FcmConfiguration,
} from '@halo/models/send-integration-configurations/native-fcm-configurations.model'
import { SendIntegrationsEditor } from '@halo/components/domain-data-form/send-integrations/send-integrations-editor'

interface ISendIntegrationsEditorProps {
    visible: boolean
    onClose: () => void
    domain: DomainDto
    onSubmit: (value: any) => any
    submitDisabled?: (value: any) => boolean
}

interface ISendIntegrationsEditorState {
    tmpValue?: DomainSendIntegrationModel
}

export class SendIntegrationsDrawer extends React.Component<
    ISendIntegrationsEditorProps,
    ISendIntegrationsEditorState
> {
    protected appState: AppState
    protected appSvc: AppService
    protected domainSvc: DomainService

    public constructor(props: ISendIntegrationsEditorProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.state = {
            tmpValue: DomainSendIntegrationModel.build(this.baseIntegrations),
        }

        this.appSvc = Container.get(AppService)
        this.domainSvc = Container.get(DomainService)

        executeCleanDrawerRender(this)
    }

    public render() {
        return (
            <Drawer
                getContainer={this.appSvc.getAppContainer}
                className={getClassNames('ddf-integrations-editor')}
                title="Update Integrations"
                onClose={this.props.onClose}
                placement="right"
                closable={true}
                width={800}
                open={this.props.visible}
                disableSubmit={this.props.submitDisabled!(this.state.tmpValue)}
                onSubmit={this.handleSubmit}
            >
                <Form
                    {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                    size="small"
                    labelAlign="left"
                    requiredMark={false}
                >
                    <SendIntegrationsEditor
                        integrations={this.state.tmpValue!}
                        domain={this.props.domain}
                        loading={false}
                        onChange={this.handleFormChange}
                        className={getClassNames('ddf-integrations-editor')}
                    />
                </Form>
            </Drawer>
        )
    }

    protected get baseIntegrations(): IDomainSendIntegrationModel {
        const { apns_configuration, native_apns_configuration, native_fcm_configuration } = this.props.domain
        const nativeFcmLegacyFlag = this.appState?.flags.findActive('FEAT_SEND_INT_NATIVE_FCM_LEGACY')?.getKey()
        const hasNativeFcmLegacyFlagEnabled = this.props.domain.flags.includes(nativeFcmLegacyFlag!)

        const nativeApnsType = native_apns_configuration.private_key_type
        const nativeApnsModel =
            nativeApnsType === SendIntegrationPrivateKeyType.P8 ? NativeP8ApnsConfiguration : NativeP12ApnsConfiguration

        const nativeFcmType = native_fcm_configuration.api_version
        const nativeFcmModel =
            nativeFcmType === FcmSendIntegrationApiVersion.LEGACY || hasNativeFcmLegacyFlagEnabled
                ? NativeLegacyFcmConfiguration
                : NativeV1FcmConfiguration

        return {
            web_apns_configuration: WebApnsConfigurationModel.build(apns_configuration ?? {}),
            native_apns_configuration: nativeApnsModel.build(native_apns_configuration ?? {}),
            native_fcm_configuration: nativeFcmModel.build(native_fcm_configuration ?? {}),
        }
    }

    protected get sendIntegrations(): DomainSendIntegrationModel {
        return this.state.tmpValue ?? DomainSendIntegrationModel.build(this.baseIntegrations ?? {})
    }

    protected handleFormChange = async (value: DomainSendIntegrationModel) => {
        await this.setState({ tmpValue: value })
    }

    protected validate(integrations: DomainSendIntegrationModel): { ok: boolean; error?: any } {
        const res: any = { ok: true, error: undefined }

        const webApnsConfig = integrations.getWebApnsConfiguration()
        const nativeApnsConfig = integrations.getNativeApnsConfiguration()
        const nativeFcmConfig = integrations.getNativeFcmConfiguration()

        const setNotOk = (reason: string) => {
            res.ok = false
            res.error = reason
        }

        if (webApnsConfig) {
            this.validateWebApnsConfig(webApnsConfig, setNotOk)
        }
        if (nativeApnsConfig) {
            this.validateNativeApnsConfig(nativeApnsConfig, setNotOk)
        }
        if (nativeFcmConfig) {
            this.validateNativeFcmConfig(nativeFcmConfig, setNotOk)
        }

        return res
    }

    protected validateWebApnsConfig = (config: WebApnsConfigurationModel, throwCb: (reason: string) => void) => {
        if (config.getIsActive()) {
            const privateKey = config.getPrivateKey()
            if (!privateKey) {
                throwCb(`Web APNS Configuration is missing values. Missing certificate and pass phrase.`)
            }

            if (!privateKey.private_key_file_url) {
                throwCb(`Web APNS Configuration is missing values. Missing certificate.`)
            }

            const pushPackage = config.getPushPackage()

            if (!pushPackage.website_push_id) {
                throwCb(`Web APNS Configuration is missing values. Missing Website Push ID.`)
            }
        }
    }

    protected validateNativeApnsConfig = (
        config: NativeP8ApnsConfiguration | NativeP12ApnsConfiguration,
        throwCb: (reason: string) => void,
    ) => {
        const missingValueErrorMessage = (field: string) =>
            `Native APNS Configuration missing value: ${field} is required.`
        if (config.getIsActive()) {
            if (!config.getPrivateKeyFileUrl()) {
                throwCb(missingValueErrorMessage('Certificate'))
            }

            if (config instanceof NativeP12ApnsConfiguration) {
                if (!config.getAppBundleId()) {
                    throwCb(missingValueErrorMessage('App Bundle ID'))
                }
            }

            if (config instanceof NativeP8ApnsConfiguration) {
                if (!config.getTeamId()) {
                    throwCb(missingValueErrorMessage('Team ID'))
                }

                if (!config.getKeyId()) {
                    throwCb(missingValueErrorMessage('Key ID'))
                }
            }
        }
    }

    protected validateNativeFcmConfig = (
        config: NativeV1FcmConfiguration | NativeLegacyFcmConfiguration,
        throwCb: (reason: string) => void,
    ) => {
        const missingValueErrorMessage = (field: string) =>
            `Native FCM Configuration missing value: ${field} is required.`

        if (config.getIsActive()) {
            if (!config.getSenderId()) {
                throwCb(missingValueErrorMessage('Sender ID'))
            }

            if (config instanceof NativeV1FcmConfiguration) {
                if (!config.getServiceAccountJsonFileUrl()) {
                    throwCb(missingValueErrorMessage('Service Account JSON File'))
                }
            } else {
                if (!config.getServerApiKey()) {
                    throwCb(missingValueErrorMessage('Server API Key'))
                }
            }
        }
    }

    protected handleSubmit = async () => {
        const sendIntegrations = this.sendIntegrations

        const res = this.validate(sendIntegrations)
        if (!res.ok) {
            simpleNotification('error', res.error)
        } else {
            const submitRes = await this.props.onSubmit?.(sendIntegrations)
            if (submitRes.ok) {
                return this.props.onClose()
            }
        }
    }
}
